import type { BottomTabNavigationOptions } from "@react-navigation/bottom-tabs";
import type { NativeStackNavigationOptions } from "@react-navigation/native-stack";

import { Font } from "@/ui/theme/typography";
import { isWeb } from "@/utils/platform.util";

const DefaultModalScreenOptions = {
  animation: "slide_from_bottom",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  headerShown: false,
  presentation: isWeb() ? "transparentModal" : "fullScreenModal",
} satisfies NativeStackNavigationOptions;

const DefaultStackScreenOptions = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  headerShown: false,
} satisfies NativeStackNavigationOptions;

const DefaultTabScreenOptions = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  headerShown: false,
  tabBarLabelStyle: {
    fontFamily: Font.BodyMedium,
  },
  ...(isWeb() && {
    tabBarPosition: "top" as const,
  }),
} satisfies BottomTabNavigationOptions;

enum TabPath {
  Explore = "explore",
  Home = "home",
  Messages = "messages",
  Profile = "profile",
  Schedule = "schedule",
  Storybook = "storybook",
}

enum ScreenPath {
  // to prevent race conditions, there should be only one pathway to route into ScreenPath.App directly
  // (from PostLoginRouting, with everything else routing to PostLoginRouting), so this value should not be reused elsewhere
  // see comment in 'app/(postLoginRouting)/_layout.tsx'
  App = "(app)",
  GroupChannel = "group-channel",
  Index = "index",
  Login = "(login)",
  Logout = "logout",
  MainTabs = "(tabs)",
  PostLoginRouting = "(postLoginRouting)",
  Root = "/",
  WebAuth0Callback = "myequip",
}

enum LoginScreenPath {
  ForgotPassword = "forgot-password",
  Login = "index",
  LoginAlias = "login",
  MFA = "mfa",
}

enum OnboardingScreenPath {
  CalendarSync = "calendar-sync",
  Consent = "consent",
  Notifications = "notifications",
  Onboarding = "(onboarding)",
  VerifyInformation = "verify-information",
  Welcome = "welcome",
}

const ModalsPath = "(modals)";

// eslint-disable-next-line no-restricted-syntax -- this is a legitimate use case
const ModalPath = {
  AboutMessages: `${ModalsPath}/about-messages`,
  AssessmentTask: `${ModalsPath}/assessment-task`,
  AssessmentWebView: `${ModalsPath}/assessment-webview`,
  BookAppointment: `${ModalsPath}/book-appointment`,
  ChannelDetails: `${ModalsPath}/channel-details`,
  EditNotificationSettings: `${ModalsPath}/edit-notification-settings`,
  EditProfile: `${ModalsPath}/edit-profile`,
  GroupClassDetails: `${ModalsPath}/group-class-details`,
  GroupClassList: `${ModalsPath}/group-class-list`,
  GroupClassListDetails: `${ModalsPath}/group-class-list-details`,
  HomeGroupClassDetails: `${ModalsPath}/explore-groups-details`,
  Journal: `${ModalsPath}/journal`,
  LabRequest: `${ModalsPath}/lab-request`,
  LogWeightTask: `${ModalsPath}/log-weight`,
  MedicalEvaluationTask: `${ModalsPath}/medical-evaluation`,
  OnboardingV2: `${ModalsPath}/onboarding-v2`,
  RecommendedGroupClass: `${ModalsPath}/recommended-group-class`,
  SafetyPlanDetails: `${ModalsPath}/safety-plan-details`,
  ScheduleEventDetails: `${ModalsPath}/schedule-event-details`,
  SignFormsTask: `${ModalsPath}/sign-forms`,
  UploadInsuranceTask: `${ModalsPath}/upload-insurance`,
  VideoContent: `${ModalsPath}/video-content`,
  WaitingRoom: `${ModalsPath}/waiting-room`,
  WeightHistory: `${ModalsPath}/weight-history`,
} as const;

type ModalPathValue = ValueOf<typeof ModalPath>;
type ValidPath = ModalPathValue | ScreenPath | TabPath;

export type { ModalPathValue, ValidPath };
export {
  DefaultModalScreenOptions,
  DefaultStackScreenOptions,
  DefaultTabScreenOptions,
  LoginScreenPath,
  ModalPath,
  OnboardingScreenPath,
  ScreenPath,
  TabPath,
};
